import React, { useEffect, useState } from 'react';
import {
  displayErrorMessage,
  displaySuccess,
} from 'Helpers/FacetNotificationManager';
import { makeStyles } from '@material-ui/core/styles';
import useCollectionDeprecated from 'Hooks/useCollectionDeprecated';
import IconButton from '@material-ui/core/IconButton';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { EmailAddressMetaOverride } from 'Resources/EmailAddress';
import EmailAddressForm from 'Components/UI/EmailAddressForm';
import PropTypes from 'prop-types';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
  },
  button: {
    margin: 0,
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));

export default function ClientEmailAddresses({ clientResource, onUpdate }) {
  const classes = useStyles();
  const { collection, setCollectionData, refreshCollection } = useCollectionDeprecated(
    {},
    { metaOverride: EmailAddressMetaOverride },
  );
  const [resources, setResources] = useState(collection.resources().list());

  useEffect(() => {
    if (!clientResource.relationship('emailAddresses').link('related')) {
      return;
    }

    clientResource
      .relationship('emailAddresses')
      .link('related')
      .Get({ raw: true })
      .then(res => setCollectionData(res))
      .catch((err) => {
        displayErrorMessage(err, err.errors);
      });
  }, [clientResource]);

  useEffect(() => {
    setResources(collection.resources().list());
  }, [collection]);

  const addEmailAddress = () => {
    const newResource = collection.create().meta().merge(EmailAddressMetaOverride).parent();
    newResource.attr('clientId', clientResource.id())
      .attr('isActive', 'true');

    setResources([...resources, newResource]);
    if (onUpdate) {
      onUpdate();
    }
  };

  const removeEmailAddress = async (index) => {
    const resource = resources[index];

    try {
      const updated = resources.slice();
      updated.splice(index, 1);
      setResources(updated);

      if (resource.link('self')) {
        await resource.delete();
        await refreshCollection();
      }

      if (onUpdate) {
        onUpdate();
      }
      if (resource.link('self')) {
        displaySuccess('Successfully deleted email address');
      }
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const onSubmit = () => {
    refreshCollection().then(() => {
      if (onUpdate) {
        onUpdate();
      }
    });
  };

  const validateSubmission = (resource) => {
    if (resource.attr('type') !== 'login') {
      return true;
    }
    const originalLoginEmailAddress = resources.find(v => v.attr('isPrimary') === 'true');
    if (originalLoginEmailAddress && originalLoginEmailAddress.id() !== resource.id()) {
      // eslint-disable-next-line no-alert
      return !!window.confirm(`Do you want to change the login email address to ${resource.attr('address')}?`);
    }
    // There was no previous login email address
    return true;
  };

  const addButtonDisabled = resources.length !== collection.resources().list().length;
  return (
    <div className={classes.root}>
      {resources.map((emailAddress, index) => (
        <div key={`emailAddress-${emailAddress.id()}`}>
          <EmailAddressForm
            validateSubmission={validateSubmission}
            emailAddressResource={emailAddress}
            onSubmit={onSubmit}
            onDelete={() => removeEmailAddress(index)}
          />
        </div>
      ))}
      <div
        className={addButtonDisabled ? '' : classes.button}
        onClick={addButtonDisabled ? () => {} : addEmailAddress}
        onKeyPress={addButtonDisabled ? () => {} : addEmailAddress}
        role="button"
        tabIndex="0"
      >
        <IconButton
          disabled={addButtonDisabled}
          color={addButtonDisabled ? 'inherit' : 'secondary'}
          aria-label="add-email-address"
        >
          <AddCircleIcon />
        </IconButton>
        Add Email Address
      </div>
    </div>
  );
}

ClientEmailAddresses.propTypes = {
  clientResource: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
};
